import { api } from './client';
import { FailureFilters, DEFAULT_FAILURE_FILTERS } from '@/types/filters';
import { transformFiltersToFailureFilter } from '@/utils/dataTransform';
import {
  VblRecord,
  VblApiResponse,
  VblAnalysisResult,
  VblAnalysisRequest,
  TurbineToForecastList
} from '@/types/vbl';
import {
  type TurbineWorkspaceMapping,
  type TurbineWorkspaceMappingsResponse,
  type TurbineWithMappingsResponse,
  type MappingStats,
  type CSVImportResult,
  type TurbineWorkspaceMappingFilter,
  CSVMappingRow,
  validateComponentSelection,
  validateUniqueRow,
  CreateConfigFromCSVResult
} from '@/types/turbineWorkspaceMapping';
import { turbinesApi } from './turbines';

// Backend response types that don't match frontend expectations
interface BackendTurbineWorkspaceConfig {
  turbine_id: string;
  turbine_name: string;
  model_name: string;
  workspace_id: string;
  workspace_name: string;
  major_component_id: string;
  major_component_name: string;
  component_id: string;
  component_name: string;
  site_id: string;
  site_name: string;
  created_at: string;
  created_by: string;
}

interface BackendTurbineWorkspaceConfigList {
  total_count: number;
  total_pages: number;
  page: number;
  size: number;
  has_more: boolean;
  config: BackendTurbineWorkspaceConfig[];
}

interface RemoveTurbineToWorkspaceConfig {
  turbine_id: string;
  workspace_id: string;
  component_id?: string;
  major_component_id?: string;
}

/**
 * VBL API Service - mirrors failuresApi structure but for VBL data
 * Uses degradation_time (detection to failure) instead of operation_time
 */
class VblApiService {
  /**
   * Get VBL data with filtering and pagination - exactly like getFailures
   */
  async getVblData(filters: FailureFilters = DEFAULT_FAILURE_FILTERS): Promise<{
    total_count: number;
    total_pages: number;
    page: number;
    size: number;
    has_more: boolean;
    vbls: VblRecord[];
  }> {
    try {
      const page = filters.page || 1;
      const size = filters.size || 20;

      // Check if any filters are applied
      const hasFilters =
        (filters.sites && filters.sites.length > 0) ||
        (filters.turbineModels && filters.turbineModels.length > 0) ||
        (filters.componentsMajor && filters.componentsMajor.length > 0) ||
        (filters.componentsMinor && filters.componentsMinor.length > 0) ||
        (filters.outcomeName && filters.outcomeName.length > 0) ||
        (filters.failureModes && filters.failureModes.length > 0) ||
        (filters.excludedCaseIds && filters.excludedCaseIds.length > 0) ||
        filters.operationTimeMin !== undefined ||
        filters.operationTimeMax !== undefined ||
        filters.dateFrom !== undefined ||
        filters.dateTo !== undefined;

      // Force use of analysis_filter for large requests (analysis purposes)
      const isAnalysisRequest = (filters.size || 20) >= 10000;

      let response: VblApiResponse;

      if (!hasFilters && !isAnalysisRequest) {
        // No filters applied and not an analysis request - use GET endpoint
        const urlWithParams = `/vbl?size=${size}&page=${page}`;
        response = await api.get<VblApiResponse>(urlWithParams);
      } else {
        // Filters applied OR analysis request - use POST filter endpoint
        const failureFilter = await transformFiltersToFailureFilter(filters);

        // Use analysis_filter for analysis requests, vbl_filter for pagination
        const endpoint = isAnalysisRequest
          ? '/vbl/filter/analysis_filter'
          : '/vbl/filter/vbl_filter';

        const urlWithParams = `${endpoint}?size=${size}&page=${page}`;
        response = await api.post<VblApiResponse>(urlWithParams, failureFilter);
      }


      // Transform VBL records to add computed fields
      const transformedVbls = response.vbls.map((vbl, _index) => {
        const transformed = {
          ...vbl,
          // Add computed fields for UI compatibility
          failure_mode: vbl.failure_mode_name || '',
          failure_date: vbl.actual_rts_date || vbl.down_date || '',
          degradation_time_years: vbl.degradation_time / 365.25, // Convert days to years
          component_major: vbl.major_component_name || '',
          component_minor: vbl.component_name || '',
        };
        return transformed;
      });

      return {
        total_count: response.total_count,
        total_pages: response.total_pages,
        page: response.page,
        size: response.size,
        has_more: response.has_more,
        vbls: transformedVbls,
      };
    } catch (error) {
      console.error('Failed to fetch VBL data:', error);
      return {
        total_count: 0,
        total_pages: 0,
        page: filters.page || 1,
        size: filters.size || 20,
        has_more: false,
        vbls: [],
      };
    }
  }

  /**
   * Get filter statistics for VBL data
   */
  async getVblFilterStatistics(filters: FailureFilters = DEFAULT_FAILURE_FILTERS): Promise<{
    totalCount: number;
    siteBreakdown: Record<string, number>;
    componentBreakdown: Record<string, number>;
    outcomeBreakdown: Record<string, number>;
    degradationTimeStats: {
      min: number;
      max: number;
      avg: number;
      median: number;
    };
  }> {
    const response = await this.getVblData({ ...filters, size: 10000, page: 1 });
    const vbls = response.vbls;

    const siteBreakdown: Record<string, number> = {};
    const componentBreakdown: Record<string, number> = {};
    const outcomeBreakdown: Record<string, number> = {};

    vbls.forEach((vbl) => {
      siteBreakdown[vbl.site_name] = (siteBreakdown[vbl.site_name] || 0) + 1;
      componentBreakdown[vbl.component_major] = (componentBreakdown[vbl.component_major] || 0) + 1;
      outcomeBreakdown[vbl.outcome_name] = (outcomeBreakdown[vbl.outcome_name] || 0) + 1;
    });

    const degradationTimes = vbls
      .filter(vbl => vbl.degradation_time > 0)
      .map((vbl) => vbl.degradation_time_years)
      .sort((a, b) => a - b);

    const median = degradationTimes.length > 0
      ? degradationTimes[Math.floor(degradationTimes.length / 2)]
      : 0;

    return {
      totalCount: vbls.length,
      siteBreakdown,
      componentBreakdown,
      outcomeBreakdown,
      degradationTimeStats: {
        min: degradationTimes[0] || 0,
        max: degradationTimes[degradationTimes.length - 1] || 0,
        avg:
          degradationTimes.length > 0
            ? degradationTimes.reduce((sum, t) => sum + t, 0) / degradationTimes.length
            : 0,
        median,
      },
    };
  }

  /**
   * Perform VBL Weibull analysis - client-side calculation
   * This would normally call a backend endpoint, but we'll handle it in the hook
   */
  async performVblAnalysis(_request: VblAnalysisRequest): Promise<VblAnalysisResult | null> {
    // This is a placeholder - actual analysis is done in useVblAnalysis hook
    // Similar to how useWeibullAnalysis works
    return null;
  }

  /**
   * Get turbines that need forecasting (have detection but no failure yet)
   */
  async getTurbinesToForecast(filters: FailureFilters = DEFAULT_FAILURE_FILTERS): Promise<TurbineToForecastList> {
    try {
      const page = filters.page || 1;
      const size = filters.size || 20;

      const urlWithParams = `/vbl/turbines_to_forecast?size=${size}&page=${page}`;
      const response = await api.get<TurbineToForecastList>(urlWithParams);

      return response;
    } catch (error) {
      console.error('Failed to fetch turbines to forecast:', error);
      return {
        total_count: 0,
        total_pages: 0,
        page: filters.page || 1,
        size: filters.size || 20,
        has_more: false,
        turbines: [],
      };
    }
  }

  /**
   * Export VBL data to CSV or JSON
   */
  async exportVblData(
    filters: FailureFilters = DEFAULT_FAILURE_FILTERS,
    format: 'csv' | 'json' = 'csv'
  ): Promise<string> {
    try {
      const response = await this.getVblData({ ...filters, size: 10000 });

      if (format === 'json') {
        return JSON.stringify(response.vbls, null, 2);
      }

      // CSV export
      const headers = [
        'Case ID',
        'Turbine Name',
        'Site Name',
        'Component',
        'Failure Mode',
        'Outcome',
        'Degradation Time (Days)',
        'Degradation Time (Years)',
        'Detection Date',
        'Down Date',
        'RTS Date'
      ];

      const csvRows = [
        headers.join(','),
        ...response.vbls.map((vbl) =>
          [
            vbl.case_id,
            vbl.turbine_name,
            vbl.site_name,
            vbl.component_major,
            vbl.failure_mode,
            vbl.outcome_name,
            vbl.degradation_time,
            vbl.degradation_time_years.toFixed(2),
            vbl.detected_date,
            vbl.down_date || '',
            vbl.actual_rts_date || ''
          ]
            .map((field) => `"${field}"`)
            .join(',')
        ),
      ];

      return csvRows.join('\n');
    } catch (error) {
      console.error('Failed to export VBL data:', error);
      return '';
    }
  }

  // Turbine Workspace Mapping methods
  async getMappings(filters: TurbineWorkspaceMappingFilter & { page?: number; size?: number } = {}, mappings: TurbineWorkspaceMapping[] = []): Promise<TurbineWorkspaceMappingsResponse> {
    try {
      const page = filters.page || 1;
      const size = filters.size || 20;

      // Call the VBL backend endpoint
      const urlWithParams = `/vbl/turbine_to_workspace_config?size=${size}&page=${page}`;

      const backendResponse = await api.get<BackendTurbineWorkspaceConfigList>(urlWithParams);

      // Transform backend response to frontend format
      mappings = [...mappings, ...backendResponse.config.map(config => this.transformBackendConfig(config))];

      // If API has more data, recursively call API
      if (backendResponse.has_more) {
        return this.getMappings({ page: page + 1, size: size }, mappings);
      }

      return {
        mappings,
        total_count: backendResponse.total_count,
        total_pages: backendResponse.total_pages,
        page: backendResponse.page,
        size: backendResponse.size,
        has_more: backendResponse.has_more,
      };
    } catch (error) {
      console.error('[vblApi.getMappings] Failed to fetch mappings:', error);
      throw error;
    }
  }

  private transformBackendConfig(config: BackendTurbineWorkspaceConfig): TurbineWorkspaceMapping {
    // Generate a mapping ID from turbine and component combination
    const mappingId = `${config.turbine_id}_${config.component_id || config.major_component_id || 'default'}`;

    return {
      mapping_id: mappingId,
      turbine_id: config.turbine_id,
      turbine_name: config.turbine_name,
      site_id: config.site_id,
      site_name: config.site_name,
      workspace_id: config.workspace_id,
      workspace_name: config.workspace_name,
      component_id: config.component_id,
      component_name: config.component_name,
      major_component_id: config.major_component_id,
      major_component_name: config.major_component_name,
      created_at: config.created_at,
      created_by: config.created_by
    };
  }

  async getTurbinesWithMappings(filters: TurbineWorkspaceMappingFilter & { page?: number; size?: number } = {}): Promise<TurbineWithMappingsResponse> {
    try {
      // This method combines turbine data with mapping data
      // For now, we'll return mappings in the expected format
      // TODO: Integrate with turbinesApi to get full turbine details

      const mappingsResponse = await this.getMappings(filters);

      // Transform mappings to TurbineWithMapping format
      const turbinesWithMappings = mappingsResponse.mappings.map(mapping => ({
        turbine_id: mapping.turbine_id,
        turbine_name: mapping.turbine_name,
        site_id: mapping.site_id || 'unknown',
        site_name: mapping.site_name || 'Unknown Site',
        cod_date: '', // Would need to come from turbines API
        model_name: '', // Would need to come from turbines API
        manufacturer_name: '', // Would need to come from turbines API
        mapping: mapping,
      }));

      return {
        turbines: turbinesWithMappings,
        total_count: mappingsResponse.total_count,
        total_pages: mappingsResponse.total_pages,
        page: mappingsResponse.page,
        size: mappingsResponse.size,
        has_more: mappingsResponse.has_more,
      };
    } catch (error) {
      console.error('[vblApi.getTurbinesWithMappings] Failed to fetch turbines with mappings:', error);
      throw error;
    }
  }

  /**
   * Create a single turbine-to-workspace mapping
   * Uses POST /vbl/turbine_to_workspace_config/new endpoint
   */
  async createMapping(mappingData: {
    turbine_id: string;
    workspace_id: string;
    component_id?: string;
    major_component_id?: string;
  }, userId: string): Promise<TurbineWorkspaceMapping> {

    const payload = {
      ...mappingData,
      owner: userId,
    };


    try {
      const response = await api.post<TurbineWorkspaceMapping>(
        '/vbl/turbine_to_workspace_config/new',
        payload
      );

      return response;
    } catch (error) {
      console.error('[vblApi.createMapping] Failed to create mapping:', error);
      console.error('[vblApi.createMapping] Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  }

  /**
   * Update an existing turbine-to-workspace mapping
   * Uses PUT /vbl/turbine_to_workspace_config/{mapping_id} endpoint
   */
  async updateMapping(mappingId: string, mappingData: {
    turbine_id: string;
    workspace_id: string;
    component_id?: string;
    major_component_id?: string;
  }, userId: string): Promise<TurbineWorkspaceMapping> {

    const payload = {
      ...mappingData,
      owner: userId,
    };

    try {
      const response = await api.put<TurbineWorkspaceMapping>(
        `/vbl/turbine_to_workspace_config/${mappingId}`,
        payload
      );

      return response;
    } catch (error) {
      console.error('[vblApi.updateMapping] Failed to update mapping:', error);
      throw error;
    }
  }

  /**
   * Delete a single turbine-to-workspace mapping
   * Uses POST /vbl/turbine_to_workspace_config/remove endpoint
   */
  async deleteMapping(mapping: TurbineWorkspaceMapping): Promise<void> {

    // Transform mapping to backend's expected format
    // Backend requires EXACTLY ONE of component_id or major_component_id to be set
    const deleteData: Partial<RemoveTurbineToWorkspaceConfig> = {
      turbine_id: mapping.turbine_id,
      workspace_id: mapping.workspace_id,
    };

    // Prefer component_id if it exists and is not zero UUID (more specific)
    // This prevents accidental deletion of multiple mappings with same major_component_id
    const zeroUuid = '00000000-0000-0000-0000-000000000000';

    if (mapping.component_id && mapping.component_id !== zeroUuid) {
      deleteData.component_id = mapping.component_id;
    } else if (mapping.major_component_id && mapping.major_component_id !== zeroUuid) {
      deleteData.major_component_id = mapping.major_component_id;
    } else {
      // Default to major_component_id with zero UUID if neither exists
      deleteData.major_component_id = zeroUuid;
    }


    try {
      await api.post('/vbl/turbine_to_workspace_config/remove', deleteData);

    } catch (error) {
      console.error('[vblApi.deleteMapping] Failed to delete mapping:', error);
      throw error;
    }
  }

  async getMappingStats(): Promise<MappingStats> {
    try {
      // Get all mappings to calculate statistics
      const mappingsResponse = await this.getMappings({ page: 1, size: 10000 });
      const mappings = mappingsResponse.mappings;

      // Fetch turbines (only need to get total count from result. Size is 1 to keep response small)
      const turbinesResponse = await turbinesApi.getTurbines({ page: 1, size: 1 });

      const uniqueWorkspaceIds = new Set(mappings.map(m => m.workspace_id));
      const uniqueTurbineIds = new Set(mappings.map(m => m.turbine_id));
      const uniqueSiteIds = new Set(mappings.map(m => m.site_id));

      const mappingsBySite: Record<string, {
        site_name: string;
        total_turbines: number;
        mapped_turbines: number;
      }> = {};

      for (const id of Array.from(uniqueSiteIds)) {
        const siteMappings = mappings.filter(m => m.site_id === id);
        const uniqueTurbineIdsForSite = new Set(siteMappings.map(m => m.turbine_id));
        const turbinesForSite = await turbinesApi.getTurbines({ page: 1, size: 1, site_id: id })

        mappingsBySite[id] = {
          site_name: siteMappings[0].site_name,
          total_turbines: turbinesForSite.total_count,
          mapped_turbines: uniqueTurbineIdsForSite.size
        };
      };

      return {
        total_turbines: turbinesResponse.total_count,
        mapped_turbines: uniqueTurbineIds.size,
        unmapped_turbines: turbinesResponse.total_count - uniqueTurbineIds.size,
        total_workspaces_used: uniqueWorkspaceIds.size,
        mappings_by_site: mappingsBySite,
      };
    } catch (error) {
      console.error('[vblApi.getMappingStats] Failed to calculate mapping stats:', error);
      throw error;
    }
  }

  async exportMappingsCSV(): Promise<string> {
    try {
      // Get all mappings for export
      const mappingsResponse = await this.getMappings({ page: 1, size: 10000 });
      const mappings = mappingsResponse.mappings;

      // Define CSV headers
      const headers = [
        'Mapping ID',
        'Turbine ID',
        'Turbine Name',
        'Site Name',
        'Workspace ID',
        'Workspace Name',
        'Component Name',
        'Major Component Name',
        'Created At',
        'Created By'
      ];

      // Generate CSV rows
      const csvRows = [
        headers.join(','),
        ...mappings.map(mapping => [
          `"${mapping.mapping_id}"`,
          `"${mapping.turbine_id}"`,
          `"${mapping.turbine_name}"`,
          `"${mapping.site_name || 'Unknown Site'}"`,
          `"${mapping.workspace_id}"`,
          `"${mapping.workspace_name}"`,
          `"${mapping.component_name || ''}"`,
          `"${mapping.major_component_name || ''}"`,
          `"${mapping.created_at}"`,
          `"${mapping.created_by}"`,
        ].join(','))
      ];

      return csvRows.join('\n');
    } catch (error) {
      console.error('[vblApi.exportMappingsCSV] Failed to export mappings to CSV:', error);
      throw error;
    }
  }

  async importMappingsCSV(csvContent: string, userId: string): Promise<CSVImportResult> {
    const lines = csvContent.split('\n').filter(line => line.trim());
    const dataRows = lines.slice(1);

    const successful: CSVMappingRow[] = [];
    const failed: { row: CSVMappingRow; errors: string[]; lineNum: number; }[] = [];

    let lineNum = 0;
    for (const line of dataRows) {
      lineNum++;
      const values = line.split(',').map(v => v.replace(/"/g, '').trim());

      const row: CSVMappingRow = {
        site_name: values[0] || '',
        turbine_name: values[1] || '',
        workspace_name: values[2] || '',
        component_name: values[3] || undefined,
        major_component_name: values[4] || undefined,
        owner: userId,
        line_num: lineNum
      };

      const errors: string[] = [];

      if (!row.site_name) errors.push('Site name is required');
      if (!row.turbine_name) errors.push('Turbine name is required');
      if (!row.workspace_name) errors.push('Workspace name is required');

      // Validate component selection using the validation function
      const componentValidationError = validateComponentSelection(
        row.component_name,
        row.major_component_name
      );
      if (componentValidationError) {
        errors.push(componentValidationError.message);
      }

      // If no errors for row, check it is not a duplicate
      if (errors.length === 0) {
        if (!validateUniqueRow(successful, row)) {
          errors.push('is a duplicate row');
        }
      }

      if (errors.length > 0) {
        failed.push({ row, errors, lineNum });
      } else {
        successful.push(row);
      }
    }

    return {
      successful,
      failed,
      summary: {
        total_rows: dataRows.length,
        successful_count: successful.length,
        failed_count: failed.length,
      },
    };
  }

  /**
   * Create multiple turbine-to-workspace mappings from valid rows imported via csv file
   * Uses POST /vbl/turbine_to_workspace_config/new/csv endpoint
   */
  async createConfigFromCSV(validRows: CSVMappingRow[]): Promise<CreateConfigFromCSVResult> {
    try {
      const response = await api.post<CreateConfigFromCSVResult>('/vbl/turbine_to_workspace_config/new/csv', validRows);
      return response
    } catch (error) {
      console.error('[vblApi.createConfigFromCSV] Failed to create mappings from csv:', error);
      console.error('[vblApi.createConfigFromCSV] Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }


  }


  /**
   * Create multiple turbine-to-workspace mappings in bulk
   * Uses POST /vbl/turbine_to_workspace_config/new/bulk endpoint
   */
  async createBulkMappings(data: {
    turbine_ids: string[];
    workspace_id: string;
    component_id?: string;
    major_component_id?: string;
  }, userId: string): Promise<TurbineWorkspaceMapping[]> {

    // Transform data to array format expected by backend
    // Backend expects an array of individual mapping objects, not a single object with arrays
    const bulkMappings = data.turbine_ids.map(turbine_id => ({
      turbine_id,
      workspace_id: data.workspace_id,
      component_id: data.component_id,
      major_component_id: data.major_component_id,
      owner: userId,
    }));


    try {
      const response = await api.post<TurbineWorkspaceMapping[]>(
        '/vbl/turbine_to_workspace_config/new/bulk',
        bulkMappings  // Send array instead of object
      );

      return response;
    } catch (error) {
      console.error('[vblApi.createBulkMappings] Failed to create bulk mappings:', error);
      console.error('[vblApi.createBulkMappings] Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  }

  /**
   * Delete multiple turbine-to-workspace mappings in bulk
   * Uses POST /vbl/turbine_to_workspace_config/remove/bulk endpoint
   */
  async deleteBulkMappings(mappings: TurbineWorkspaceMapping[]): Promise<void> {

    // Transform mappings to backend's expected format
    // Backend expects array of RemoveTurbineToWorkspaceConfig objects
    // Each object must have EXACTLY ONE of component_id or major_component_id set
    const bulkRemoveData = mappings.map(mapping => {
      const data: Partial<RemoveTurbineToWorkspaceConfig> = {
        turbine_id: mapping.turbine_id,
        workspace_id: mapping.workspace_id,
      };

      // Prefer component_id if it exists and is not zero UUID (more specific)
      // This prevents accidental deletion of multiple mappings with same major_component_id
      const zeroUuid = '00000000-0000-0000-0000-000000000000';

      if (mapping.component_id && mapping.component_id !== zeroUuid) {
        data.component_id = mapping.component_id;
      } else if (mapping.major_component_id && mapping.major_component_id !== zeroUuid) {
        data.major_component_id = mapping.major_component_id;
      } else {
        // Default to major_component_id with zero UUID if neither exists
        data.major_component_id = zeroUuid;
      }

      return data;
    });


    try {
      await api.post('/vbl/turbine_to_workspace_config/remove/bulk', bulkRemoveData);

    } catch (error) {
      console.error('[vblApi.deleteBulkMappings] Failed to delete bulk mappings:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const vblApi = new VblApiService();